// src/components/ParticipantLadder.jsx
//
// Ladder bar chart for participant types (Receptive / Active breakdown).
// Styled to match ActivityTypeLadder — two groups, each with sub-type rows.
//
// Three-way view toggle:
//   Total           — all participants regardless of count method
//   Ticketed / reg. — activities where attendanceTicketed (outputFormatId=2) > 0
//   Not ticketed    — remainder (Total minus Ticketed), computed on the frontend
//
// Data shape expected (from /report-builder/charts/participant-types):
//   types: [
//     { formatId: 3, name: 'Receptive (free)',   group: 'Receptive',
//       total: N, ticketedTotal: N },
//     { formatId: 4, name: 'Receptive (paying)', group: 'Receptive',
//       total: N, ticketedTotal: N },
//     { formatId: 5, name: 'Active (free)',       group: 'Active',
//       total: N, ticketedTotal: N },
//     { formatId: 6, name: 'Active (paying)',     group: 'Active',
//       total: N, ticketedTotal: N },
//     { formatId: 7, name: 'Active (paid)',       group: 'Active',
//       total: N, ticketedTotal: N },
//   ]

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import '../charts/charts.scss';

const MAX_BAR_PCT = 93;

const GROUP_COLOR = {
  Receptive: '#D92B2C',
  Active: '#922020',
};

const TOGGLE_OPTS = [
  { key: 'total', label: 'Total' },
  { key: 'ticketed', label: 'Ticketed / registered' },
  { key: 'notTicketed', label: 'Not ticketed' },
];

const fmtN = (n) => (n == null ? '' : Number(n).toLocaleString());
const fmtPct = (n, d) => (d > 0 ? `${Math.round((n / d) * 100)}%` : '0%');

/** Return the view-appropriate value for a single type row. */
function getVal(type, view) {
  if (view === 'ticketed') return Number(type.ticketedTotal) || 0;
  if (view === 'notTicketed') {
    return Math.max(
      0,
      (Number(type.total) || 0) - (Number(type.ticketedTotal) || 0)
    );
  }
  return Number(type.total) || 0;
}

/**
 * Rule-based 2–3 sentence plain-language description of the participant data.
 * Mirrors the style of `buildProseSummary` in OutcomeRatings — no LLM involved.
 * Updates when the view toggle changes (total / ticketed / notTicketed).
 */
function buildParticipantProse(t, data, view) {
  const types = Array.isArray(data?.types) ? data.types : [];
  if (types.length === 0) return null;

  // group is a fixed value from the API, not a display label, so don't translate it
  const receptive = types
    .filter((type) => type.group === 'Receptive')
    .reduce((s, type) => s + getVal(type, view), 0);
  const active = types
    .filter((type) => type.group === 'Active')
    .reduce((s, type) => s + getVal(type, view), 0);
  const total = receptive + active;

  const viewLabel =
    view === 'ticketed'
      ? t('charts.participant_ladder.from_ticketed_and_reg_events')
      : view === 'notTicketed'
        ? t('charts.participant_ladder.from_non_ticketed_events')
        : '';

  if (total === 0) {
    const category =
      view === 'ticketed'
        ? t('charts.participant_ladder.ticketed_registered')
        : view === 'notTicketed'
          ? t('charts.participant_ladder.non_ticketed')
          : '';
    return category
      ? t('charts.participant_ladder.no_participant_data_recorded_for', {
          category,
        })
      : null;
  }

  // Sentence 1 — overall total with receptive / active split
  const s1 = t('charts.participant_ladder.participant_data_shows', {
    numPeople: fmtN(total),
    // the template has no space before it, so an empty label leaves none
    fromEventType: viewLabel ? ` ${viewLabel}` : '',
    numReceptive: fmtN(receptive),
    numActive: fmtN(active),
  });

  // Sentence 2 — largest individual category
  const largest = [...types].sort(
    (a, b) => getVal(b, view) - getVal(a, view)
  )[0];
  const largestVal = getVal(largest, view);
  const s2 =
    largestVal > 0
      ? t('charts.participant_ladder.largest_category', {
          name: largest.name,
          num: fmtN(largestVal),
          pct: fmtPct(largestVal, total),
        })
      : null;

  // Sentence 3 — receptive / active context (total view only, when both present)
  const s3 =
    view === 'total' && receptive > 0 && active > 0
      ? t('charts.participant_ladder.receptive_participant_accounts', {
          pct: fmtPct(receptive, total),
        })
      : null;

  return [s1, s2, s3].filter(Boolean).join(' ');
}

// ─── Single type row ─────────────────────────────────────────────────────────

function TypeRow({ type, color, maxVal, view }) {
  const val = getVal(type, view);
  const isUsed = val > 0;
  const pct = isUsed ? (val / maxVal) * MAX_BAR_PCT : 0;
  const isTopBar = isUsed && val === maxVal;

  return (
    <div className="chart-bar-row">
      <div
        className={`chart-bar-row__label${
          isUsed ? '' : ' chart-bar-row__label--unused'
        }`}
      >
        {type.name}
      </div>
      <div className="chart-bar-row__bar-area">
        {isUsed ? (
          <>
            <div
              className={`chart-bar-row__bar${
                isTopBar ? ' chart-bar-row__bar--top' : ''
              }`}
              style={{
                background: color,
                ...(!isTopBar && { width: `${pct}%` }),
              }}
            />
            <span className="chart-bar-row__count">{fmtN(val)}</span>
          </>
        ) : (
          <div className="chart-bar-row__bar-spacer" />
        )}
      </div>
    </div>
  );
}

// ─── Group header ─────────────────────────────────────────────────────────────

function GroupHeader({ group, total }) {
  return (
    <div className="chart-group__header">
      <span className="chart-group__name">{group}</span>
      <span className="chart-group__count">{fmtN(total)}</span>
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

export function ParticipantLadder({ data, hideUnused = false }) {
  const { t } = useTranslation();
  const [view, setView] = useState('total');
  const types = data?.types || [];

  const groups = [
    {
      group: t('charts.participant_ladder.groups.receptive'),
      types: types.filter((t) => t.group === 'Receptive'),
    },
    {
      group: t('charts.participant_ladder.groups.active'),
      types: types.filter((t) => t.group === 'Active'),
    },
  ]
    .map((g) => ({
      ...g,
      groupTotal: g.types.reduce((s, t) => s + getVal(t, view), 0),
    }))
    .sort((a, b) => b.groupTotal - a.groupTotal);

  // Max across all individual type rows (for proportional bar widths)
  const maxVal = Math.max(...types.map((t) => getVal(t, view)), 1);

  if (types.length === 0) {
    return (
      <div className="chart-empty chart-empty--centered">
        {t('charts.participant_ladder.no_participant_data_available')}
      </div>
    );
  }

  return (
    <div>
      {/* Three-way view toggle */}
      <div className="pl-toggle">
        {TOGGLE_OPTS.map((opt) => (
          <button
            key={opt.key}
            className={`pl-toggle__btn${
              view === opt.key ? ' pl-toggle__btn--active' : ''
            }`}
            onClick={() => setView(opt.key)}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Group bars with sub-type rows */}
      <div className="chart-bar-list">
        {groups
          // In ticketed/notTicketed views always show all groups so the layout
          // remains visible even when every value is zero.
          .filter((g) => view !== 'total' || !hideUnused || g.groupTotal > 0)
          .map((g) => {
            const color = GROUP_COLOR[g.group] || '#9ca3af';
            const allRows = g.types;
            const used = allRows
              .filter((t) => getVal(t, view) > 0)
              .sort((a, b) => getVal(b, view) - getVal(a, view));
            // In ticketed/notTicketed views keep zero-value rows visible too.
            const unused =
              hideUnused && view === 'total'
                ? []
                : allRows.filter((t) => getVal(t, view) === 0);

            return (
              <div key={g.group} className="chart-group">
                <GroupHeader group={g.group} total={g.groupTotal} />
                {[...used, ...unused].map((t) => (
                  <TypeRow
                    key={t.formatId}
                    type={t}
                    color={color}
                    maxVal={maxVal}
                    view={view}
                  />
                ))}
              </div>
            );
          })}
      </div>

      {/* Rule-based prose summary */}
      {buildParticipantProse(t, data, view) && (
        <p className="chart-prose">{buildParticipantProse(t, data, view)}</p>
      )}
    </div>
  );
}
